import React, { useState, useEffect } from 'react';
import { DrawerProps } from '@mui/material';
import styled from '@emotion/styled';
import { DrawerPaperView } from './toolympus/components/frame/new';
import { useDrawerColors, BaseColors } from './useDrawerColors';
import { ThemeSettings } from './theme';

interface Props extends DrawerProps {
  mobile?: boolean;
  open: boolean;
  width?: number;
  shadow?: boolean;
}

const DrawerPaperX = styled(DrawerPaperView)`
  &[aria-hidden="true"] {
    z-index: -1;
  }
  
  & .MuiPaper-root {
    /* Set base background color, but gradients are moved to pseudo-elements */
    background-color: ${ThemeSettings.colors.blue_grey_dark};
    box-shadow: ${props => props.shadow ? '0px 0px 5px 1px #33333330' : 'none'};
    color: ${ThemeSettings.colors.background};
    
    /* Create a stacking context so z-index: -1 stays inside the drawer */
    position: relative;
    z-index: 0;

    /* == CROSSFADE LAYERS == */
    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      z-index: -1; /* Push gradients behind the content */
      background-size: cover;
      background-repeat: no-repeat;
      transition: opacity 10s ease-in-out; /* The magic smooth flow */
    }

    &::before {
      background-color: var(--bg0-a);
      background-image: 
        radial-gradient(ellipse 120% 60% at -30% -10%, var(--bg1-a) 20%, transparent 120%),
        radial-gradient(ellipse 120% 60% at 65% 12%, var(--bg2-a) 20%, transparent 120%),
        radial-gradient(ellipse 180% 80% at 65% 130%, var(--bg3-a) 40%, transparent 70%);
      opacity: var(--opacity-a);
    }

    &::after {
      background-color: var(--bg0-b);
      background-image: 
        radial-gradient(ellipse 120% 60% at -30% -10%, var(--bg1-b) 20%, transparent 120%),
        radial-gradient(ellipse 120% 60% at 65% 12%, var(--bg2-b) 20%, transparent 120%),
        radial-gradient(ellipse 180% 80% at 65% 130%, var(--bg3-b) 40%, transparent 70%);
      opacity: var(--opacity-b);
    }

    & .MuiSvgIcon-root { color: ${ThemeSettings.colors.background}; }
    & .icon-custom { height: 20px; }

    & .Mui-selected {
      color: ${ThemeSettings.colors.background};
      background: #ffffff40;
      & .MuiSvgIcon-root { color: ${ThemeSettings.colors.background}; }
    }

    & > * {
      max-width: 100%;
      overflow: hidden;
    }

    & .MuiList-root {
      width: 60px;
      overflow: hidden;
    }
    @media(max-width: 660px) {
      max-width: 60px;
    }
  }
`;

export const AppDrawerColored = (props: Props) => {
  const { drawerColors } = useDrawerColors();
  const currentColors = drawerColors || BaseColors;
  
  // Stringify to prevent endless useEffect loops if the hook returns a new array reference
  const colorsStr = JSON.stringify(currentColors);

  // Track two layers. One fades in while the other stays visible in the background.
  const [layers, setLayers] = useState({
    colorsA: currentColors,
    colorsB: currentColors,
    activeLayer: 'A'
  });

  useEffect(() => {
    setLayers(prev => {
      // Don't swap if colors are exactly the same
      if (prev.activeLayer === 'A' && JSON.stringify(prev.colorsA) === colorsStr) return prev;
      if (prev.activeLayer === 'B' && JSON.stringify(prev.colorsB) === colorsStr) return prev;

      // Apply new colors to the currently hidden layer, then activate it
      return {
        ...prev,
        ...(prev.activeLayer === 'A' ? { colorsB: currentColors } : { colorsA: currentColors }),
        activeLayer: prev.activeLayer === 'A' ? 'B' : 'A'
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorsStr]);

  return (
    <DrawerPaperX 
      {...props} 
      shadow 
      PaperProps={{
        ...(props.PaperProps || {}),
        style: {
          // Standard CSS variables (No @property syntax needed)
          '--bg0-a': layers.colorsA[0],
          '--bg1-a': layers.colorsA[1],
          '--bg2-a': layers.colorsA[2],
          '--bg3-a': layers.colorsA[3],
          '--opacity-a': layers.activeLayer === 'A' ? 1 : 0,

          '--bg0-b': layers.colorsB[0],
          '--bg1-b': layers.colorsB[1],
          '--bg2-b': layers.colorsB[2],
          '--bg3-b': layers.colorsB[3],
          '--opacity-b': layers.activeLayer === 'B' ? 1 : 0,
          ...(props.PaperProps?.style || {})
        } as React.CSSProperties
      }}
    >
      {props.children}
    </DrawerPaperX>
  );
}